import { Allotment } from 'allotment'
import { useEffect, useState } from 'react'

import { UserTypeEnum } from '@/entities/drivers/models/Driver'
import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { DropdownOption } from '@/entities/dropdowns/models/DropdownOption'
import { groupsSelectors } from '@/entities/groups'
import { fetchWebAdmins, saveWebAdmin, toggleWebAdminStatus, webAdminsSelectors } from '@/entities/webAdmins'
import { Button, Icon, IconButton, ToggleButton } from '@/shared/components'
import { DynamicForm } from '@/shared/components/DynamicForm/DynamicForm'
import { CustomGridToolbar } from '@/shared/components/GridTable/CustomGridToolbar'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faAdd, faClose, faEdit, faKey, faToggleOn } from '@fortawesome/pro-regular-svg-icons'
import { Divider, Grid, styled } from '@mui/material'
import { GridColDef, GridInputRowSelectionModel, GridValidRowModel } from '@mui/x-data-grid-premium'
import { EntityId } from '@reduxjs/toolkit'

import { getWebAdminFormDefinition, initialWebAdminFormValues } from './libs/getWebAdminFormDefinition'
import {
  StyledAddButton, StyledButtonsContainer, StyledContainer, StyledDescription, StyledHeader, StyledLeftPane, StyledRightPane,
} from './StyledOutletPage'

export type WebAdminsPageProps = {
  //
}

export const WebAdminsPage: React.FC<WebAdminsPageProps> = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridInputRowSelectionModel>()
  const [showDetails, setShowDetails] = useState(false)
  const [formType, setFormType] = useState<'add' | 'edit' | 'view' | 'reset-password'>('add')
  const [isSubmitting, setSubmitting] = useState(false)
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(false)

  const dispatch = useAppDispatch()
  const groups = useAppSelector(state => groupsSelectors.selectAll(state))
  const webAdmins = useAppSelector(state => webAdminsSelectors.selectAll(state))
  const groupOptions: DropdownOption[] = groups.map(group => ({ label: group.name, value: group.id }))
  const { data: roleOptions } = useDropdown('roles')

  const { setToastContent } = useToastContext()

  // Fetch web admins from server
  const fetchAdminsFromServer = async () => {
    setLoading(true)
    await dispatch(fetchWebAdmins())
    setLoading(false)
  }

  useEffect(() => {
    fetchAdminsFromServer()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  // Define the form fields and contents
  const formDefinition = getWebAdminFormDefinition({
    defaultValues: initialWebAdminFormValues,
    selectOptions: {
      roleId: roleOptions,
      groupIds: groupOptions,
    },
    isResetPassword: formType === 'reset-password',
    showPasswordFields: formType === 'add',
    isReadOnly: isSubmitting || !showDetails || formType === 'view',
  })

  const handleSubmit = (data: Record<string, unknown>) => {
    setSubmitting(true)

    const selectedAdmin = webAdmins.find(admin => admin.id === rowSelectionModel)

    if (formType === 'edit' && selectedAdmin) {
      // Handle edit web admin
      const editPayload = {
        ...selectedAdmin,
        ...data,
        roleId: (data.roleId as DropdownOption).value,
        groupIds: (data.groupIds as DropdownOption[]).map(g => g.value),
      }

      dispatch(saveWebAdmin({
        webAdmin: editPayload,
      })).then(() => {
        setFormType('view')
      })
    } else {
      // Handle add web admin
      const existingAdmin = webAdmins.find(admin => admin.login === data.login)

      if (existingAdmin) {
        setToastContent({
          open: true,
          severity: 'error',
          title: 'Save Admin',
          content: 'An admin with the same Login already exists.',
        })

        setSubmitting(false)

        return
      }

      const addPayload = {
        ...data,
        roleId: (data.roleId as DropdownOption).value,
        groupIds: (data.groupIds as DropdownOption[]).map(g => g.value),
        userType: UserTypeEnum.WebAdmin,
      }

      dispatch(saveWebAdmin({
        webAdmin: addPayload,
      })).unwrap().then((webAdmin) => {
        setFormType('view')
        setRowSelectionModel(webAdmin.id)
      })
    }

    setSubmitting(false)
  }

  // Update form values when selectedId changes
  const resetForm = () => {
    if (formType === 'add') {
      setFormValues(initialWebAdminFormValues)
    } else {
      const selectedAdmin = webAdmins.find(admin => admin.id === rowSelectionModel)

      if (selectedAdmin) {
        setFormValues({
          ...selectedAdmin,
          roleId: roleOptions.find(r => r.value === selectedAdmin.roleId),
          groupIds: groupOptions.filter(g => selectedAdmin.groupIds.includes(g.value)),
        })
      }
    }
  }

  useEffect(() => {
    if (!rowSelectionModel || !['edit', 'view', 'reset-password'].includes(formType)) return

    resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelectionModel, webAdmins, formType, setFormValues])

  // Define the columns for the grid
  const rows = webAdmins.map(item => ({
    ...item,
    isSelectable: true,
  }))

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      filterable: true,
    },
    {
      field: 'login',
      headerName: 'Login',
      width: 200,
      filterable: true,
    },
    {
      field: 'roleId',
      headerName: 'Role',
      width: 150,
      filterable: true,
      valueGetter: (value: EntityId) => {
        return roleOptions.find(r => r.value === value)?.label || ''
      },
    },
    {
      field: 'groupIds',
      headerName: 'Groups',
      width: 250,
      filterable: true,
      valueGetter: (value: EntityId[]) => {
        return value
          .map(id => groupOptions.find(g => g.value === id)?.label)
          .filter(Boolean)
          .join(', ')
      },
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      filterable: true,
      valueGetter: (value) => value ? 'Active' : 'Disabled',
    },
  ]

  const onViewRowDetail = (row: GridValidRowModel) => {
    setFormType('view')
    setShowDetails(true)
    setRowSelectionModel(row.id)
  }

  const onHandleAdd = () => {
    setFormType('add')
    setShowDetails(true)
    setRowSelectionModel(undefined)
    setFormValues(initialWebAdminFormValues)
  }

  const onHandleResetPassword = () => {
    setFormType('reset-password')
    setFormValues({
      ...formValues,
      password: '',
      confirmPassword: '',
    })
  }

  const handleToggleStatus = async (id: string) => {
    try {
      await dispatch(toggleWebAdminStatus({ id }))
      setToastContent({
        open: true,
        severity: 'success',
        title: 'Success',
        content: 'Web Admin status updated successfully',
      })
    } catch (error) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Error',
        content: 'Failed to update depot status',
      })
    }
  }

  const handleCloseDetail = () => {
    setRowSelectionModel(undefined)
    setShowDetails(false)
  }

  return (
    <StyledContainer>
      <Allotment separator={true} defaultSizes={[100, 100]}>
        <Allotment.Pane>
          <StyledLeftPane>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1">Web Admins</StyledHeader>
                <StyledDescription>
                  Manage web administrators and their access rights. Configure roles, permissions, and group access.
                </StyledDescription>
              </Grid>
              <Grid item>
                <StyledAddButton
                  primary
                  startIcon={<Icon icon={faAdd} />}
                  onClick={onHandleAdd}
                  sx={{ minWidth: pxToRem(130) }}
                >
                  New Admin
                </StyledAddButton>
              </Grid>
            </Grid>
            <Divider sx={{ marginTop: pxToRem(16) }} />
            <StyledGridTable
              rows={rows}
              columns={columns}
              loading={loading}
              isDragDropEnabled={false}
              rowSelectionModel={rowSelectionModel}
              onRowClick={onViewRowDetail}
              initialState={{
                filter: {
                  filterModel: {
                    items: [{
                      field: 'isActive', operator: 'equals', value: 'Active',
                    }],
                  },
                },
              }}
              slots={{
                toolbar: CustomGridToolbar,
              }}
              sx={{
                '.MuiDataGrid-filler--pinnedRight': { display: 'none' },
              }}
            />
          </StyledLeftPane>
        </Allotment.Pane>
        <Allotment.Pane visible={showDetails} minSize={360}>
          <StyledRightPane $visible={showDetails}>
            <Grid container direction={'row'} alignItems={'flex-start'} justifyContent={'space-between'} wrap="nowrap">
              <Grid item>
                <StyledHeader variant="body1" sx={{ paddingLeft: 0 }}>
                  {formType === 'add' && 'Add a new admin'}
                  {formType === 'edit' && `Edit ${formValues?.name || ''} details`}
                  {formType === 'view' && `View ${formValues?.name || ''} details`}
                  {formType === 'reset-password' && `Reset password for ${formValues?.name || ''}`}
                </StyledHeader>
              </Grid>
              <StyledButtonsContainer item>
                {formType === 'view' && (
                  <>
                    <ToggleButton
                      value="active"
                      primary
                      text
                      selected={false}
                      onChange={() => handleToggleStatus(rowSelectionModel as string)}
                    >
                      <Icon icon={faToggleOn} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                      Toggle Status
                    </ToggleButton>
                    <Button
                      outlined
                      onClick={onHandleResetPassword}
                    >
                      <Icon icon={faKey} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                      Reset Password
                    </Button>
                    <ToggleButton
                      value="checked"
                      primary
                      text
                      selected={false}
                      onChange={() => {
                        resetForm()
                        setFormType('edit')
                      }}
                      sx={{ minWidth: pxToRem(100) }}
                    >
                      <Icon icon={faEdit} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                      Edit Admin
                    </ToggleButton>
                  </>
                )}
                {(formType === 'edit' || formType === 'reset-password') && (
                  <ToggleButton
                    value="checked"
                    primary
                    text
                    selected={true}
                    onChange={() => {
                      resetForm()
                      setFormType('view')
                    }}
                    sx={{ minWidth: pxToRem(100) }}
                  >
                    <Icon icon={faEdit} sx={{ fontSize: pxToRem(14), marginRight: pxToRem(4) }} />
                    Cancel
                  </ToggleButton>
                )}
                <IconButton size="small" onClick={() => handleCloseDetail()}>
                  <Icon icon={faClose} sx={{ fontSize: pxToRem(18) }} />
                </IconButton>
              </StyledButtonsContainer>
            </Grid>
            <DynamicForm
              {...formDefinition}
              initialValues={formValues}
              onSubmit={handleSubmit}
              isReadOnly={isSubmitting || !showDetails || formType === 'view'}
              onCancel={() => {
                resetForm()

                if (formType === 'add') {
                  setShowDetails(false)
                }

                setFormType('view')
              }}
              slotProps={{
                submitButton: {
                  disabled: isSubmitting,
                },
              }}
            />
          </StyledRightPane>
        </Allotment.Pane>
      </Allotment>
    </StyledContainer>
  )
}

const StyledGridTable = styled(GridTable)`
  height: calc(100% - ${pxToRem(86)});
`